import { get, toQueryParams } from '@/lib/http';
import { endpoints } from './endpoints';
import type {
  BuyerWiseQr,
  DailyActivityPoint,
  DashboardSummary,
  DateRange,
  PoSummaryRow,
  QrStatusOverview,
  RecentActivity,
  TodaySummary,
} from '@/types';

/**
 * Inclusive `yyyy-MM-dd` bounds from the topbar filter.
 *
 * Both optional: send neither and every endpoint returns its unfiltered view,
 * which is what the dashboard asks for by default.
 */
export interface DashboardRangeParams {
  from?: string;
  to?: string;
}

export const dashboardApi = {
  summary: (params: DashboardRangeParams = {}) =>
    get<DashboardSummary>(endpoints.dashboard.summary, { params: toQueryParams(params) }),

  dailyActivity: (range: DateRange = '7d', params: DashboardRangeParams = {}) =>
    get<DailyActivityPoint[]>(endpoints.dashboard.dailyActivity, {
      // `range` is ignored server-side once from/to are present; sending it anyway
      // keeps the preset intact for when the filter is cleared.
      params: toQueryParams({ range, ...params }),
    }),

  qrStatus: (params: DashboardRangeParams = {}) =>
    get<QrStatusOverview>(endpoints.dashboard.qrStatus, { params: toQueryParams(params) }),

  buyerWise: (period: DateRange = 'month', limit = 5, params: DashboardRangeParams = {}) =>
    get<BuyerWiseQr[]>(endpoints.dashboard.buyerWise, {
      params: toQueryParams({ period, limit, ...params }),
    }),

  recentActivities: (limit = 5, params: DashboardRangeParams = {}) =>
    get<RecentActivity[]>(endpoints.dashboard.recentActivities, {
      params: toQueryParams({ limit, ...params }),
    }),

  poSummary: (limit = 5, params: DashboardRangeParams = {}) =>
    get<PoSummaryRow[]>(endpoints.dashboard.poSummary, {
      params: toQueryParams({ limit, ...params }),
    }),

  todaySummary: (params: DashboardRangeParams = {}) =>
    get<TodaySummary>(endpoints.dashboard.todaySummary, { params: toQueryParams(params) }),
};
